import { Gender, OtherSubjectsForm, PersonalDetailsForm, ReasonForm, Reason, Bank } from '@ha/domain';
import { appointmentFormRegex, maxLength } from '@ha/hypotheken-afspraak.constants';

import { inject, Injectable } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';

import { ApFormService } from '@ap/form';
import { AddressForm, ApDutchAddressService } from '@ap/dutch-address';
import { ApContactDetailsService, ContactDetailsForm } from '@ap/personal-details';

@Injectable({ providedIn: 'root' })
export class FormService {
    private readonly apFormService = inject(ApFormService);
    private readonly contactDetailsService = inject(ApContactDetailsService);
    private readonly dutchAddressService = inject(ApDutchAddressService);

    createDutchAddressFormGroup(): FormGroup<AddressForm> {
        return this.dutchAddressService.createFormGroup(true);
    }

    createContactDetailsFormGroup(): FormGroup<ContactDetailsForm> {
        return this.contactDetailsService.createFormGroup();
    }

    createReasonFormGroup(): FormGroup<ReasonForm> {
        return new FormGroup<ReasonForm>({
            reason: new FormControl<Reason>(null, Validators.required),
            houseReason: new FormControl(null),
            refinance: new FormControl(null)
        });
    }

    createPersonalDetailsFormGroup(): FormGroup<PersonalDetailsForm> {
        return new FormGroup<PersonalDetailsForm>({
            gender: new FormControl<Gender>(null, Validators.required),
            firstName: new FormControl<string>(null, [
                Validators.maxLength(maxLength.default),
                Validators.pattern(appointmentFormRegex.namePattern),
                Validators.required
            ]),
            initials: new FormControl<string>(null, [
                Validators.maxLength(maxLength.default),
                Validators.required,
                Validators.pattern(appointmentFormRegex.namePattern)
            ]),
            familyName: new FormControl<string>(null, [
                Validators.maxLength(maxLength.default),
                Validators.pattern(appointmentFormRegex.namePattern),
                Validators.required
            ]),
            prefix: new FormControl<string>(null, [
                Validators.maxLength(maxLength.default),
                Validators.pattern(appointmentFormRegex.namePattern)
            ]),
            dateOfBirth: new FormControl<Date>(null, Validators.required)
        });
    }

    createOtherSubjectsFormGroup(): FormGroup<OtherSubjectsForm> {
        return new FormGroup<OtherSubjectsForm>({
            existingCustomer: new FormControl<boolean>(null, Validators.required),
            bank: new FormControl<Bank>(null),
            otherSubjects: new FormControl<string>(null, [
                Validators.maxLength(maxLength.otherSubjects),
                Validators.pattern(appointmentFormRegex.otherSubjectPattern)
            ])
        });
    }

    verifyFormGroups(
        reasonFormGroup: FormGroup<ReasonForm>,
        personalDetailsFormGroup: FormGroup<PersonalDetailsForm>,
        addressFormGroup: FormGroup<AddressForm>,
        contactDetailsFormGroup: FormGroup<ContactDetailsForm>,
        otherSubjectsFormGroup: FormGroup<OtherSubjectsForm>
    ): boolean {
        const verifyReason = this.apFormService.verify(reasonFormGroup);
        const verifyPersonalDetails = this.apFormService.verify(personalDetailsFormGroup);
        const verifyAddress = this.apFormService.verify(addressFormGroup);
        const verifyContactDetails = this.apFormService.verify(contactDetailsFormGroup);
        const verifyOtherSubjects = this.apFormService.verify(otherSubjectsFormGroup);

        return (
            verifyReason.valueOf() &&
            verifyPersonalDetails.valueOf() &&
            verifyAddress.valueOf() &&
            verifyContactDetails.valueOf() &&
            verifyOtherSubjects.valueOf()
        );
    }
}
